/**
 * Recent searches — the palette's idle state.
 *
 * localStorage, not the URL: the app's rule is "URL for anything shareable,
 * localStorage only for per-person display habits" (InventoryPage), and your last
 * five queries are the second thing. Versioned key so a shape change is a reset
 * rather than a parse error, and every read is guarded — a hand-edited or
 * half-written entry must degrade to "no recents", never throw inside a render.
 */

const KEY = 'infras.searchRecents.v1';
const CAP = 5;

export interface Recent {
  /** The raw query, scope prefix included — replaying it should reproduce the search. */
  q: string;
  /** Epoch ms, for ordering. */
  at: number;
  /** Hit count at the time it was run; omitted when the search errored. */
  hits?: number;
}

export function loadRecents(): Recent[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((r): r is Recent => !!r && typeof r.q === 'string' && typeof r.at === 'number')
      .slice(0, CAP);
  } catch {
    return [];
  }
}

/**
 * Record a query. De-duplicated on the trimmed string so re-running yesterday's
 * search moves it to the top instead of filling the list with itself, and capped
 * at five — a "recent" list you have to read is not doing its job.
 */
export function pushRecent(q: string, hits?: number): Recent[] {
  const trimmed = q.trim();
  if (trimmed.length < 2) return loadRecents();
  const next = [
    { q: trimmed, at: Date.now(), hits },
    ...loadRecents().filter((r) => r.q.toLowerCase() !== trimmed.toLowerCase()),
  ].slice(0, CAP);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota — recents are a convenience, never a hard failure */
  }
  return next;
}
